import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import en from "./en.json"
import hy from "./hy.json"
import ru from "./ru.json"

// Translation resources
const resources = {
    en: {
        translation: en,
    },
    hy: {
        translation: hy,
    },
    ru: {
        translation: ru,
    },
}

// Language mapping to handle locale variations
const languageMapping = {
    "en-US": "en",
    "en-GB": "en",
    "en-CA": "en",
    "hy-AM": "hy",
    "ru-RU": "ru",
    "ru-BY": "ru",
    "ru-KZ": "ru",
}

// Custom language detector that normalizes language codes
const customLanguageDetector = {
    name: "customDetector",

    lookup: (options) => {
        // Get language from various sources
        const sources = [
            // URL query parameter
            new URLSearchParams(window.location.search).get("lng"),
            // localStorage
            localStorage.getItem("i18nextLng"),
            // Browser language
            navigator.language,
            navigator.languages?.[0],
        ]

        for (const source of sources) {
            if (source) {
                // Normalize the language code
                const normalizedLang = normalizeLanguageCode(source)
                if (normalizedLang) {
                    console.log(`Language detected: ${source} -> ${normalizedLang}`)
                    return normalizedLang
                }
            }
        }

        return "en" // fallback
    },

    cacheUserLanguage: (lng) => {
        localStorage.setItem("i18nextLng", lng)
    },
}

// Create a proper detector plugin
const customDetectorPlugin = {
    type: "languageDetector",
    async: false,
    init: (services, detectorOptions, i18nextOptions) => {
        // Initialize if needed
    },
    detect: customLanguageDetector.lookup,
    cacheUserLanguage: customLanguageDetector.cacheUserLanguage,
}

// Function to normalize language codes
const normalizeLanguageCode = (langCode) => {
    if (!langCode) return null

    // Convert to lowercase and handle common formats
    const normalized = langCode.toLowerCase().replace("_", "-")

    // Direct match
    if (["en", "hy", "ru"].includes(normalized)) {
        return normalized
    }

    // Check mapping for locale variations
    const mapped = languageMapping[normalized]
    if (mapped) {
        return mapped
    }

    // Extract base language (e.g., 'en' from 'en-US')
    const baseLang = normalized.split("-")[0]
    if (["en", "hy", "ru"].includes(baseLang)) {
        return baseLang
    }

    return null
}

i18n
    .use(customDetectorPlugin) // Use custom detector plugin
    .use(LanguageDetector) // Fallback to default detector
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: "en",
        supportedLngs: ["en", "hy", "ru"], // Explicitly define supported languages
        nonExplicitSupportedLngs: true, // Allow language variants
        load: "languageOnly", // Load only language, not locale (en instead of en-US)

        interpolation: {
            escapeValue: false,
        },

        detection: {
            order: ["querystring", "localStorage", "navigator", "htmlTag"],
            lookupQuerystring: "lng",
            lookupLocalStorage: "i18nextLng",
            caches: ["localStorage"],
            excludeCacheFor: ["cimode"], // Don't cache in development mode

            // Convert language codes to supported format
            convertDetectedLanguage: (lng) => {
                const normalized = normalizeLanguageCode(lng)
                console.log(`Converting detected language: ${lng} -> ${normalized || "en"}`)
                return normalized || "en"
            },
        },

        // Debug settings
        debug: process.env.NODE_ENV === "development",

        // React specific options
        react: {
            useSuspense: false, // Disable suspense to avoid loading issues
            bindI18n: "languageChanged loaded",
            bindI18nStore: "added removed",
            transEmptyNodeValue: "",
            transSupportBasicHtmlNodes: true,
            transKeepBasicHtmlNodesFor: ["br", "strong", "i", "em"],
        },
    })
    .then((t) => {
        console.log("i18n initialized successfully")
        console.log("Current language:", i18n.language)
        console.log("Available languages:", Object.keys(i18n.services.resourceStore.data))
    })
    .catch((error) => {
        console.error("i18n initialization failed:", error)
    })

// Add language change listener for debugging
i18n.on("languageChanged", (lng) => {
    console.log("Language changed to:", lng)
    document.documentElement.lang = lng
})

// Add missing key handler
i18n.on("missingKey", (lng, namespace, key, fallbackValue) => {
    console.warn(`Missing translation key: ${key} for language: ${lng}`)
})

export default i18n
